import { actions as buildsActions } from '@saucelabs/sl-module-builds';
import { actions as jobActions, operations as jobOperations } from '@saucelabs/sl-module-jobs';
import { actions as notificationActions } from '@saucelabs/sl-container-notifications';
import { constants as notificationConstants } from '@saucelabs/sl-module-notifications';
import { utils as authUtils } from '@saucelabs/sl-container-authentication';
import { createApiAction } from '@saucelabs/sl-util-redux';
import { parse } from 'qs';
import throttle from 'lodash.throttle';
import { actions as performanceActions } from '@saucelabs/sl-module-performance';
import { actions as errorPageActions } from '@saucelabs/sl-container-error-pages';
import { api as userApi } from '@saucelabs/sl-module-user';

import { actions as commandActions } from './../batch-framework-commands';
import { actions as fileActions, constants as fileConstants, utils as fileUtils } from './../file';
import { actions as containerJobActions, operations as containerJobOperations } from './../job';
import * as actions from './actions';
import * as constants from './constants';
import * as types from './types';
import { getNewActiveCommandId, hasUserAccessToTheJob, updateVideoTimeline, parseSauceTestReport } from './utils';
import {
  commandSelector,
  filteredCommandsSelector,
  location,
  playerSelector,
  videoPlayerJobDataSelector,
  currentTimeSelector,
  containerSelector,
} from './selectors';
import {
  API_DEVICE_TYPE_TO_TRACKING_DEVICE_TYPE_CONVERSION,
  JOB_DETAILS_EVENTS,
  JOB_DETAILS_LEGACY_EVENTS,
  TEST_DETAILS_TRACKING_DEFAULT_PROPS,
  trackEvent,
} from './../../tracking';
import { actions as buildsVDCActions } from './../build/vdc';
import { actions as buildsRDCActions } from './../build/rdc';
import {
  functions as webdriverFunctions,
  actions as webdriverCommandActions,
} from './../../webdriver-commands';

import { operations } from './index';

export const getJob = (payload, meta) => (dispatch) => (
  dispatch(jobOperations.getJob(payload, meta))
);

export const getAsset = (payload) => (dispatch) => (
  dispatch(jobActions.getAsset(payload, { skipErrorPage: true }))
);

export const rdcAssetHeaders = (payload) => (dispatch) => (
  dispatch(jobActions.rdcAssetHeaders(payload, { skipErrorPage: true }))
    .catch(() => {
      const notification = dispatch(notificationActions.notifyDanger(
        payload.url, 'Failed to fetch test report asset.', true,
      ));
      dispatch(actions.appendNotification(notification.payload.id));
    })
);

export const getRdcAsset = (payload) => (dispatch) => (
  dispatch(jobActions.getRdcAsset(payload, { skipErrorPage: true }))
    .catch(() => {
      const notification = dispatch(notificationActions.notifyDanger(
        payload.url, 'Failed to fetch test report asset.', true,
      ));
      dispatch(actions.appendNotification(notification.payload.id));
    })
);

export const fetchJob = (payload) => (dispatch) => (
  dispatch(operations.getJob(payload, { skipErrorPage: true }))
    .catch((slError) => (
      dispatch(jobActions.getUPJob(payload))
        .catch(() => {
          throw slError;
        })
    ))
);

/**
 * Fetch openSauceUser when job is public and user is not authenticated
 * @param {Object} payload
 * @param {string} payload.username
 * @returns {Promise}
 */
export const fetchOpenSauceUser = (payload) => (dispatch) => (
  userApi.getRestoUser(payload)
    .then(({ data }) => (
      dispatch(actions.setOpenSauceUser(data))
    )).catch(() => {
      // User is not openSource, don't do anything
      // PublicJob belongs to non oss user
    })
);

export const initialize = createApiAction(
  types.INITIALIZE,
  (payload, meta, dispatch, getState) => {
    const initialState = getState();
    const {
      user,
      router: { location: { pathname: currentPathname, search, state } },
    } = initialState;

    const qs = parse(search, { ignoreQueryPrefix: true });
    const authInQS = !!qs.auth;

    return dispatch(operations.fetchJob(payload))
      .then((response) => {
        const job = getState().apps.job.job.get('job');
        if ((!user?.isAuthenticated() && !authInQS) && !job.isPublic()) {
          authUtils.redirectToMainLogin(currentPathname);
          throw new Error('User not authenticated');
        }

        if (user && !hasUserAccessToTheJob({ user, job })) {
          return dispatch(errorPageActions.showNotFoundPage());
        }

        // fetch openSauceUser info if job is public and user is not authenticated
        if (job.isPublic() && !user?.isAuthenticated()) {
          dispatch(operations.fetchOpenSauceUser({ username: job.get('owner') }));
        }

        const trackingDeviceType = API_DEVICE_TYPE_TO_TRACKING_DEVICE_TYPE_CONVERSION[
          job.getDeviceType()
        ];

        dispatch(
          trackEvent(JOB_DETAILS_LEGACY_EVENTS.PAGE_OPEN, {
            ...TEST_DETAILS_TRACKING_DEFAULT_PROPS,
            deviceType: trackingDeviceType,
            error: response.error,
            isJSFrameworks: job.isJSFrameworks(),
            isPuppeteerReplay: job.isPuppeteerReplay(),
            // NOTE: state is pushed by the JobCard component into the router when arriving
            // NOTE: at the job details from the jobs list.
            isFromJobListView: state?.fromJobListView ?? false,
            isHeadless: job.isHeadless(),
            isRdc: job.isRdc(),
            jobType: job.getJobType(),
            status: job.getConsolidatedStatus(),
          }),
        );
        return response;
      }).catch((errResponse) => {
        dispatch(
          trackEvent(JOB_DETAILS_LEGACY_EVENTS.PAGE_OPEN, {
            ...TEST_DETAILS_TRACKING_DEFAULT_PROPS,
            error: errResponse?.error,
          }),
        );
        if (!user?.isAuthenticated()) {
          authUtils.redirectToMainLogin(currentPathname);
        }
        throw errResponse;
      });
  },
  { meta: { autoHandleError: true } },
);

// If job has commands, returns the filename of commands log
// Otherwise, returns the filename of first found log
export const getPrimaryLogFilename = (job, assets) => {
  if (job.hasCommands()) {
    if (job.isEspresso()) {
      return fileConstants.ESPRESSO_LOGFILE_NAME;
    }
    if (job.isXcuiTest()) {
      return fileConstants.XCUITEST_LOGFILE_NAME;
    }
    return fileConstants.SELENIUM_LOGFILE_NAME;
  }
  if (job.isJSFrameworks() || job.isPuppeteerReplay()) {
    return fileConstants.DEVX_LOGFILE_NAME;
  }

  const logFileEntries = fileUtils.logFilesOnly(assets);
  if (!logFileEntries.length) {
    return null;
  }
  const [[, firstLogFilename]] = logFileEntries;
  return firstLogFilename;
};

export const parseLogfile = (job, logData) => (dispatch) => {
  if (job.isEspresso()) {
    return dispatch(commandActions.parseEspressoLog(logData));
  }
  if (job.isXcuiTest()) {
    return dispatch(commandActions.parseXCUITestLog(logData));
  }
  return dispatch(webdriverCommandActions.parseSeleniumLog(logData));
};

export const getBuild = (job, isUnauthenticated = false) => (dispatch) => {
  const params = {
    id: job.get('id'),
  };

  // If user is not authenticated, we assume it's the OSS version of app
  if (isUnauthenticated) {
    params.isOpenSource = true;
    params.username = job.get('owner');
  }
  const buildAction = job.isRdc() ? buildsRDCActions : buildsVDCActions;
  return dispatch(buildAction.getBuildByJobId(params, {
    skipErrorPage: true,
    autoHandleError: true,
  }));
};

export const tryGetBuildByName = (job) => (dispatch) => {
  if (!job.getBuildName()) {
    return Promise.resolve();
  }

  return dispatch(buildsActions.getBuildByName({
    buildName: job.getBuildName(),
    username: job.get('owner'),
  }, { skipErrorPage: true }));
};

export const fetchConsoleLogAsset = ({ job }) => (dispatch) => {
  if (job.isExtendedDebugging() && job.hasExtendedDebuggingSupport()) {
    dispatch(jobActions.setAssetList({ 'console.json': 'console.json' }));
  }
};

const fetchSauceTestReportJson = ({ auth, config, jobId }) => (dispatch) => {
  const logfileName = 'sauce-test-report.json';

  return dispatch(operations.getAsset(
    { id: jobId, auth, assetName: logfileName, config },
    { skipErrorPage: true },
  ))
    .then((asset) => {
      dispatch(fileActions.attachFile({ name: logfileName, data: asset.data }));

      const testReport = parseSauceTestReport(asset.data);
      dispatch(containerJobActions.showTestReport(testReport));
    })
    .catch(() => {
      dispatch(containerJobActions.noTestCasesAvailable());
    });
};

const fetchJUnitXMLAsset = ({ auth, config, jobId }) => (dispatch) => {
  const logfileName = 'junit.xml';

  return dispatch(operations.getAsset(
    { id: jobId, auth, assetName: logfileName, config },
    { skipErrorPage: true },
  ))
    .then((asset) => {
      dispatch(fileActions.attachFile({ name: logfileName, data: asset.data }));
      dispatch(commandActions.parseJUnit(asset.data));
    })
    .catch(() => {
      dispatch(containerJobActions.noTestCasesAvailable());
    });
};

let tryGetSLAssetsTimeout;
export const tryGetSLAssets = ({ auth, job, config }) => (dispatch) => {
  if (!job.showAssets()) {
    clearTimeout(tryGetSLAssetsTimeout);
    dispatch(containerJobActions.assetsExpired());
    const notification = dispatch(notificationActions.notifyDanger(
      constants.MISSING_ASSETS_MESSAGE, 'Test assets have expired.', true,
    ));
    dispatch(trackEvent(JOB_DETAILS_EVENTS.EXPIRED_ASSETS, TEST_DETAILS_TRACKING_DEFAULT_PROPS));
    dispatch(actions.appendNotification(notification.payload.id));
    return Promise.resolve();
  }

  const { id } = job;
  return dispatch(jobActions.getAssetsList({ id, config }, { skipErrorPage: true }))
    .then(({ data }) => {
      clearTimeout(tryGetSLAssetsTimeout);
      const logfileName = operations.getPrimaryLogFilename(job, data);
      dispatch(operations.fetchConsoleLogAsset({ job }));

      if (job.isJSFrameworks()) {
        if ('sauce-test-report.json' in data) {
          dispatch(fetchSauceTestReportJson({ auth, config, jobId: id }));
        } else if ('junit.xml' in data) {
          dispatch(fetchJUnitXMLAsset({ auth, config, jobId: id }));
        } else {
          dispatch(containerJobActions.noTestCasesAvailable());
        }
      }

      return dispatch(operations.getAsset(
        { id, auth, assetName: logfileName, config },
        { skipErrorPage: true },
      ))
        .then((logFile) => {
          dispatch(fileActions.attachFile({ name: logfileName, data: logFile.data }));
          dispatch(fileActions.selectFile(logfileName));
          if (job.hasCommands()) {
            dispatch(operations.parseLogfile(job, logFile.data));
          } else if (!job.isJSFrameworks()) {
            dispatch(containerJobActions.noCommandsAvailable());
          }
        });
    })
    .catch(({ response }) => {
      if (response && response.data && response.data === constants.ASSETS_NOT_AVAILABLE_YET) {
        tryGetSLAssetsTimeout = setTimeout(() => {
          dispatch(tryGetSLAssets({ job, config }));
        }, constants.API_FETCH_INTERVAL);
      }
    });
};

export const fetchSLJobAdditionalData = ({ auth, job, config }) => (dispatch) => (
  Promise.all([
    dispatch(operations.tryGetSLAssets({ auth, job, config })),
  ])
);

export const fetchRdcJobAdditionalData = ({ job, rdcMaxLogSize }) => (dispatch, getState) => {
  const assetsInfo = {};
  const assetKey = (job.manual && job.isRdc()) ? 'manualRdc' : job.automation_backend;
  const {
    logsList = [],
    selector = () => { },
  } = constants.jobAssetsList[assetKey] || {};

  if (job.video_url) {
    assetsInfo.video = job.video_url;
  }

  if (job.screenshots.length) {
    assetsInfo.screenshots = job.screenshots;
  }

  if (job.device_events_url) {
    dispatch(operations.getRdcAsset({
      url: job.device_events_url,
      config: constants.deviceEvents.config,
    })).then((response) => {
      if (response?.data) {
        dispatch(constants.deviceEvents.parser(response.data));
      }
    });
  }

  if (logsList.length) {
    // async fetching the logs and displaying as the logs becomes available
    logsList.forEach(({ assetName, urlKey }) => {
      if (job.get(urlKey)) {
        assetsInfo[assetName] = assetName;
      }
    });

    dispatch(jobActions.setAssetList(assetsInfo));

    const assetNames = logsList.filter((log) => log.assetName)
      .map((log) => log.assetName);
    const initiallySelectedAssetName = assetNames
      .indexOf(constants.DEFAULT_SELECTED_JOB_ASSET_NAME) !== -1 ?
      constants.DEFAULT_SELECTED_JOB_ASSET_NAME : assetNames[0];

    logsList.forEach(({
      assetName,
      formatter,
      parser,
      urlKey,
      config = {},
    }) => (
      job.get(urlKey) && dispatch(operations.rdcAssetHeaders({
        url: job.get(urlKey),
        config,
      })).then(
        (res) => {
          const fileSize = res.headers['content-length'];
          if (rdcMaxLogSize && fileSize && +fileSize > rdcMaxLogSize) {
            dispatch(fileActions.exceedFileSize({
              name: assetName,
            }));

            const { selectedFilename } = selector(getState());
            if (!selectedFilename && assetName === initiallySelectedAssetName) {
              dispatch(fileActions.selectFile(initiallySelectedAssetName));
            }
          } else {
            dispatch(operations.getRdcAsset({
              url: job.get(urlKey),
              config,
            })).then(
              (response) => {
                if (response?.data) {
                  // assetName exists only in order to attach a file in Logs Tab
                  if (assetName) {
                    dispatch(fileActions.attachFile({
                      name: assetName,
                      data: formatter ? formatter(response.data) : response.data,
                    }));

                    // parser exists for native framework test-cases or appium commands
                    if (parser) {
                      dispatch(parser(response.data));
                    }
                    const { selectedFilename } = selector(getState());

                    // to select first file as it becomes available
                    if (!selectedFilename && assetName === initiallySelectedAssetName) {
                      dispatch(fileActions.selectFile(initiallySelectedAssetName));
                    }
                  } else if (parser) {
                    dispatch(parser(response.data));
                  }
                }
              },
            );
          }
        },
      )),
    );
  } else {
    // to hide the loading state
    dispatch(fileActions.noFilesAvailable());
    dispatch(containerJobActions.noTestCasesAvailable());
  }
};

export const fetchAdditionalData = ({
  auth,
  job,
  config,
  isOpenSauceUser,
  rdcMaxLogSize,
}) => (dispatch) => {
  dispatch(operations.getBuild(job, isOpenSauceUser));
  if (job.isRdc()) {
    return dispatch(operations.fetchRdcJobAdditionalData({ job, rdcMaxLogSize }));
  }

  return dispatch(operations.fetchSLJobAdditionalData({ auth, job, config }));
};

const throttledNotifyLinkCopied = throttle(
  (dispatch) => dispatch(notificationActions.notify({
    category: notificationConstants.CATEGORY_SUCCESS,
    text: 'URL address is copied to your clipboard.',
  })),
  constants.NOTIFICATION_THROTTLE_INTERVAL,
  { trailing: false },
);

const throttledNotifyLinkFailedToCopy = throttle(
  (dispatch) => dispatch(notificationActions.notify({
    category: notificationConstants.CATEGORY_SUCCESS,
    text: 'URL address is copied to your clipboard.',
  })),
  constants.NOTIFICATION_THROTTLE_INTERVAL,
  { trailing: false },
);

export const notifyLinkCopied = () => (dispatch) => throttledNotifyLinkCopied(dispatch);

export const notifyLinkFailedToCopy = () => (dispatch) => throttledNotifyLinkFailedToCopy(dispatch);

const throttledUpdateActiveCommand = throttle(
  (dispatch, payload) => dispatch(containerJobOperations.updateActiveCommand(payload)),
  500,
  { trailing: false },
);

export const onVideoPlayerMounted = ({ player }) => (dispatch, getState) => {
  const jobData = videoPlayerJobDataSelector(getState());
  const { job } = containerSelector(getState());

  dispatch(actions.videoPlayerMounted(player));

  // espresso and xcuitest don't have timestamps for testCases
  if (job.isEspresso() || job.isXcuiTest()) {
    return;
  }

  player.on('timeupdate', () => (
    throttledUpdateActiveCommand(
      dispatch,
      {
        currentTime: player.currentTime(),
        timestamp: jobData.startTime ? jobData.startTime + (player.currentTime() * 1000) : null,
      },
    )
  ));
};

export const videoJumpToCommand = (command) => (dispatch, getState) => {
  const player = playerSelector(getState());
  // if we return `timelineSeconds`, then it will start on the last milisecond of
  // previous command. Adding small offset to be sure we will select a proper command.
  const currentTime = command.getTimeline() + 0.0001;
  updateVideoTimeline({ player, currentTime });
  dispatch(containerJobOperations.updateActiveCommand({
    commandId: command.id,
  }));
};

export const videoJumpToCommandId = (commandId) => (dispatch, getState) => {
  const command = commandSelector(commandId)(getState());
  if (command) {
    dispatch(operations.videoJumpToCommand(command));
  }
};

export const updateActiveVideoCommand = (payload) => (dispatch, getState) => {
  const { commands, currentTime, player } = currentTimeSelector(getState(), payload);
  const command = webdriverFunctions.findActiveCommand({ commands, currentTime });
  dispatch(containerJobOperations.updateActiveCommand({
    commandId: command.get('id'),
  }));
  updateVideoTimeline({ player, currentTime });
};

export const videoPlayPause = () => (dispatch, getState) => {
  const player = playerSelector(getState());

  if (player.paused()) {
    player.play();
  } else {
    player.pause();
  }
};

const navigateCommandByOffset = (offset) => (dispatch, getState) => {
  const commands = filteredCommandsSelector(getState());
  const hash = parseInt(location(getState()).hash?.replace('#', ''), 10);
  const activeId = Number.isNaN(hash) ? -1 : hash;
  const newActiveId = getNewActiveCommandId(commands, offset, activeId);
  dispatch(operations.videoJumpToCommandId(newActiveId));
  // Updating the video triggers active command update anyway, but it's too slow:
  dispatch(containerJobOperations.updateActiveCommand({
    collapseCurrent: true,
    commandId: newActiveId,
  }));
};

export const navigateCommandPrevious = () => navigateCommandByOffset(-1);
export const navigateCommandNext = () => navigateCommandByOffset(1);

export const setFilterPhrase = (phrase) => actions.updateCommandsFilter({ phrase });
export const setHasPerformance = (
  (hasPerformance) => actions.updateCommandsFilter({ hasPerformance })
);

export const setHasScreenshot = (
  (hasScreenshot) => actions.updateCommandsFilter({ hasScreenshot })
);

export const setHasRootCause = (
  (hasRootCause) => actions.updateCommandsFilter({ hasRootCause })
);

export const setOnlyFailedFilter = (
  (showFailedTests) => actions.updateCommandsFilter({ showFailedTests })
);

export const cleanup = ({ notifications }) => (dispatch) => (
  Promise.all([
    dispatch(actions.cleanup()),
    dispatch(fileActions.cleanup()),
    dispatch(containerJobActions.cleanup()),
    dispatch(performanceActions.cleanup()),
    ...notifications.map((notificationId) => (
      dispatch(notificationActions.hideNotification(notificationId))
    )),
  ])
);

export const createLiveSessionPreviewNotification = (isFreeUser) => (dispatch) => {
  let description = 'Some features will be disabled until the test has finished running.';
  if (!isFreeUser) {
    description += ' You can click on video to interact and take over the running test';
  }

  const notification = dispatch(notificationActions.notifyInfo(
    description,
    'This test is currently running...',
    true,
  ));
  dispatch(actions.appendNotification(notification.payload.id));
  return () => (
    dispatch(notificationActions.hideNotification(notification.payload.id))
  );
};

export const updateLogsViewerState = (payload) => throttle((dispatch) => {
  setTimeout(() => dispatch(actions.updateLogsViewerState(payload)), 0);
},
500,
{
  trailing: false,
});
